import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { styled, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { IdeaSource } from "../types";
// #region: Styled Components
const BarBox = styled(Box)(({ theme }) => ({
    width: "100%",
    marginTop: theme.spacing(1),
}));
const Column = styled(Box)(() => ({
    flex: 1,
    display: "flex",
    flexDirection: "column",
}));
const CountText = styled(Box)(({ theme }) => ({
    minWidth: 128,
    textAlign: "right",
    whiteSpace: "nowrap",
    fontSize: "1rem",
    color: theme.palette.text.secondary,
}));
const Dot = styled("span", { shouldForwardProp: (prop) => prop !== "bg" })(({ bg }) => ({
    width: 10,
    height: 10,
    borderRadius: 999,
    background: bg,
    display: "inline-block",
}));
// Hatching keeps the row's rhythm without implying a measured value of zero.
const HatchedBar = styled(Box)(({ theme }) => ({
    height: 10,
    borderRadius: 6,
    backgroundImage: `repeating-linear-gradient(-45deg, ${theme.palette.divider} 0 2px, transparent 2px 6px)`,
}));
const LabelSmall = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 120,
    fontSize: "1.05rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
}));
const PctText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: "1.05rem",
    fontWeight: 600,
    minWidth: 56,
    textAlign: "right",
}));
const RightGroup = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(2),
}));
const RowWrap = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
}));
const StyledLinearProgress = styled(LinearProgress, {
    shouldForwardProp: (prop) => prop !== "barcolor",
})(({ theme, barcolor }) => ({
    height: 10,
    borderRadius: 6,
    backgroundColor: theme.palette.action.hover,
    "& .MuiLinearProgress-bar": {
        backgroundColor: barcolor,
    },
}));
const TopRow = styled(Box)(() => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
}));
// #endregion: Types
// A full frontier is exact, so "100.0%" would claim a precision the value does not have.
const formatPct = (pct) => (pct === 100 ? "100%" : `${pct.toFixed(1)}%`);
// A rate needs a denominator: with no ideas submitted there is nothing to show but a placeholder.
const ScoreboardRow = ({ count, pct, source, total }) => {
    const theme = useTheme();
    const isHuman = source === IdeaSource.HUMAN;
    const color = isHuman ? theme.palette.warning.main : theme.palette.primary.main;
    const label = isHuman ? "Human" : "AI";
    // The bar carries its own accessible name; the row itself is grouped under the label.
    const barLabel = `${label} Pareto rate`;
    const pctClamped = Math.min(Math.max(pct, 0), 100);
    const hasIdeas = total > 0;
    return (_jsx(RowWrap, { role: "group", "aria-label": label, children: _jsxs(Column, { children: [_jsxs(TopRow, { children: [_jsxs(LabelSmall, { children: [_jsx(Dot, { bg: color }), label] }), _jsxs(RightGroup, { children: [_jsx(PctText, { variant: "body2", children: hasIdeas ? formatPct(pctClamped) : "—" }), _jsx(CountText, { children: hasIdeas ? `${count} of ${total} optimal` : "No ideas yet" })] })] }), _jsx(BarBox, { children: hasIdeas ? (_jsx(StyledLinearProgress, { variant: "determinate", value: pctClamped, barcolor: color, "aria-label": barLabel })) : (_jsx(HatchedBar, { "aria-label": barLabel })) })] }) }));
};
export default ScoreboardRow;
